import { Dialog } from '@neo4j-ndl/react';
import { useActiveProject, useOpsContext } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import React, { useState } from 'react';

import { useSelectedInstance } from '../../../shared/hooks/use-selected-instance';
import type { NewInstanceProps } from './configure-target-migration-dialog-step';
import { ConfigureTargetMigrationDialogStep } from './configure-target-migration-dialog-step';
import { CredentialsMigrationDialogStep } from './credentials-migration-dialog-step';
import { ReadBeforeMigrationDialogStep } from './read-before-migration-dialog-step';
import { SetReadonlyDialogStep } from './set-readonly-dialog-step';

export interface MigrationModalProps {
  onClose: () => void;
  onDownload: () => void;
  open: boolean;
  loading: boolean;
  error: Error | null;
  liveMigration: boolean;
  name: string;
}

export const MigrationModal = ({
  open,
  onClose,
  onDownload,
  loading,
  error,
  liveMigration,
  name,
}: MigrationModalProps) => {
  const [step, setStep] = useState(0);
  const [instanceProps, setInstanceProps] = useState<NewInstanceProps | null>(null);

  const project = useActiveProject();
  const inst = useSelectedInstance()!;
  const { selectedInstanceId } = useOpsContext();

  const handleAcceptingNewInstanceCredentials = (credentials: NewInstanceProps | null) => {
    if (isNullish(credentials)) {
      // this happens on "clone to existing"
      onClose();
      return;
    }
    setStep(3);
    setInstanceProps(credentials);
  };

  return (
    <Dialog isOpen={open} onClose={onClose}>
      {step === 0 && (
        <ReadBeforeMigrationDialogStep
          liveMigration={liveMigration}
          onAccept={() => (liveMigration ? setStep(1) : setStep(2))}
          onDecline={onClose}
          instanceId={selectedInstanceId!}
        />
      )}
      {step === 1 && <SetReadonlyDialogStep onAccept={() => setStep(2)} onDecline={onClose} />}
      {step === 2 && (
        <ConfigureTargetMigrationDialogStep
          liveMigration={liveMigration}
          onAccept={handleAcceptingNewInstanceCredentials}
          onDecline={onClose}
          name={name}
          project={project}
          instance={inst}
        />
      )}
      {step === 3 && !isNullish(instanceProps) && (
        <CredentialsMigrationDialogStep
          boltUrl={instanceProps.boltUrl}
          id={instanceProps.id}
          password={instanceProps.password}
          username={instanceProps.username}
          name={instanceProps.name}
          onAccept={onDownload}
          onDecline={onClose}
          liveMigration={liveMigration}
          databaseName={instanceProps.databaseName}
        />
      )}
    </Dialog>
  );
};
